import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, Sparkles, Lock, ArrowRight, Check,
  KeyRound, Fingerprint, Mail, Eye, EyeOff, User
} from "lucide-react";
import { toast } from "sonner";
import { puvioService } from "../genesis/identity/puvioService";
import { analyticsService } from "../genesis/analytics/analyticsService";
import RecoverySheet from "../components/RecoverySheet";
import BrandLogo from "../components/BrandLogo";
import { api } from "../lib/api";

export default function LoginPage() {
  const [puvioLoading, setPuvioLoading] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);

  // Email+password state
  const [mode, setMode]           = useState("login");
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [name, setName]           = useState("");
  const [showPw, setShowPw]       = useState(false);
  const [epLoading, setEpLoading] = useState(false);

  // Forgot password state
  const [fpOpen, setFpOpen]       = useState(false);
  const [fpEmail, setFpEmail]     = useState("");
  const [fpLoading, setFpLoading] = useState(false);
  const [fpSent, setFpSent]       = useState(false);

  const startPuvio = async () => {
    if (puvioLoading) return;
    setPuvioLoading(true);
    try { analyticsService.track("auth.puvio.start", "auth", { surface: "login" }).catch(() => {}); } catch { /**/ }
    puvioService.signInWithGoogle();
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Enter your email"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (mode === "register" && !name.trim()) { toast.error("Enter your name"); return; }
    setEpLoading(true);
    try {
      const endpoint = mode === "register" ? "/auth/email/register" : "/auth/email/login";
      const payload  = mode === "register"
        ? { email: email.trim(), password, name: name.trim() }
        : { email: email.trim(), password };
      const res = await api.post(endpoint, payload);
      if (res.data?.user_id) {
        toast.success(mode === "register" ? "Account created! Welcome to Knya." : "Welcome back!");
        window.location.replace("/");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map(d => d.msg || d).join(" ")
        : "Something went wrong. Try again.";
      toast.error(msg);
    } finally {
      setEpLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!fpEmail.trim()) { toast.error("Enter your email"); return; }
    setFpLoading(true);
    try {
      await api.post("/auth/password-reset/request", { email: fpEmail.trim() });
      setFpSent(true);
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setFpLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" data-testid="login-page">
      <div className="absolute inset-0 -z-10 opacity-90" style={{ background: "var(--gradient-hero)" }} />
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl -z-10" />
      <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-secondary/15 blur-3xl -z-10" />

      {/* Header */}
      <div className="px-6 pt-12 relative z-10 flex items-center gap-2.5" data-testid="login-header">
        <BrandLogo size={40} className="shadow-soft" />
        <span className="font-extrabold text-xl tracking-tight">Knya</span>
      </div>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="px-6 mt-10 relative z-10"
      >
        <span className="inline-flex items-center gap-1.5 bg-secondary text-secondary-foreground rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider">
          <Sparkles className="h-3 w-3" /> Women's wellness OS
        </span>
        <h1 className="mt-4 text-[38px] leading-[1.05] tracking-tight font-extrabold">
          Women's health,<br />
          <span className="text-gradient-bloom">understood.</span>
        </h1>
        <div className="mt-4 flex items-center gap-5 text-[11px] font-bold uppercase tracking-wider text-foreground/55">
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Private</span>
          <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-primary" /> Free forever</span>
        </div>
      </motion.div>

      <div className="flex-1" />

      {/* Sign-in block */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="px-6 pb-10 relative z-10"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 32px)" }}
      >
        <div className="bg-card rounded-[28px] p-5 border border-border shadow-card">

          {/* Forgot-password inline panel */}
          <AnimatePresence mode="wait">
            {fpOpen ? (
              <motion.div key="fp" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                {fpSent ? (
                  <div className="text-center py-4 space-y-2" data-testid="fp-sent-confirm">
                    <p className="text-2xl">📬</p>
                    <p className="font-extrabold text-[15px]">Check your inbox</p>
                    <p className="text-[13px] text-muted-foreground">A reset link was sent to <strong>{fpEmail}</strong>. Check your spam folder too.</p>
                    <button type="button" onClick={() => { setFpOpen(false); setFpSent(false); setFpEmail(""); }}
                      className="mt-3 text-[12px] text-primary font-bold hover:underline" data-testid="fp-back-to-login">
                      Back to sign in
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-3" data-testid="fp-form">
                    <p className="font-extrabold text-[15px]">Forgot password?</p>
                    <p className="text-[12px] text-muted-foreground">Enter your email and we'll send you a reset link.</p>
                    <div className="relative">
                      <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="email" autoFocus
                        placeholder="your@email.com"
                        value={fpEmail}
                        onChange={e => setFpEmail(e.target.value)}
                        className="w-full h-[50px] rounded-2xl border border-border bg-background pl-10 pr-4 text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                        data-testid="fp-email-input"
                      />
                    </div>
                    <button type="submit" disabled={fpLoading}
                      className="w-full h-[50px] rounded-full bg-primary text-primary-foreground font-bold text-[14px] flex items-center justify-center gap-2 disabled:opacity-60"
                      data-testid="fp-send-button">
                      {fpLoading
                        ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : <>Send reset link <ArrowRight size={14} /></>}
                    </button>
                    <button type="button" onClick={() => setFpOpen(false)}
                      className="w-full text-[12px] text-muted-foreground hover:text-foreground transition-colors" data-testid="fp-cancel">
                      Back to sign in
                    </button>
                  </form>
                )}
              </motion.div>
            ) : (
              <motion.div key="main" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {/* Mode toggle */}
                <div className="flex bg-muted rounded-2xl p-1 mb-5" data-testid="auth-mode-toggle">
                  {["login", "register"].map(m => (
                    <button key={m} type="button" onClick={() => setMode(m)}
                      className={`flex-1 h-9 rounded-xl text-[13px] font-bold transition-all ${
                        mode === m ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`auth-mode-${m}`}>
                      {m === "login" ? "Sign in" : "Create account"}
                    </button>
                  ))}
                </div>

                {/* Email + password form */}
                <form onSubmit={handleEmailAuth} className="space-y-3" data-testid="email-auth-form">
                  <AnimatePresence>
                    {mode === "register" && (
                      <motion.div key="name-field"
                        initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}>
                        <div className="relative">
                          <User size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <input type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)}
                            className="w-full h-[50px] rounded-2xl border border-border bg-background pl-10 pr-4 text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                            data-testid="register-name-input" />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="relative">
                    <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)}
                      className="w-full h-[50px] rounded-2xl border border-border bg-background pl-10 pr-4 text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                      data-testid="email-input" autoComplete="email" />
                  </div>

                  <div className="relative">
                    <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input type={showPw ? "text" : "password"} placeholder="Password" value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="w-full h-[50px] rounded-2xl border border-border bg-background pl-10 pr-11 text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                      data-testid="password-input" autoComplete={mode === "register" ? "new-password" : "current-password"} />
                    <button type="button" onClick={() => setShowPw(v => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1} data-testid="toggle-password-visibility">
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>

                  <button type="submit" disabled={epLoading}
                    className="w-full h-[52px] rounded-full bg-primary text-primary-foreground font-bold text-[15px] flex items-center justify-center gap-2 transition-all hover:opacity-95 disabled:opacity-60 active:scale-[0.99]"
                    data-testid="email-auth-submit">
                    {epLoading
                      ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <>{mode === "login" ? "Sign in" : "Create account"} <ArrowRight size={15} /></>}
                  </button>
                </form>

                {mode === "login" && (
                  <button type="button" onClick={() => setFpOpen(true)}
                    className="mt-2 w-full text-[12px] text-muted-foreground hover:text-primary transition-colors text-center font-medium"
                    data-testid="forgot-password-link">
                    Forgot your password?
                  </button>
                )}

                {/* OR divider */}
                <div className="my-4 flex items-center gap-3">
                  <span className="flex-1 h-px bg-border" />
                  <span className="text-[10px] uppercase tracking-wider font-extrabold text-muted-foreground">or</span>
                  <span className="flex-1 h-px bg-border" />
                </div>

                {/* Puvio ID */}
                <button type="button" onClick={startPuvio} disabled={puvioLoading}
                  className="group w-full h-[50px] rounded-full bg-secondary text-secondary-foreground flex items-center gap-3 pl-2 pr-5 font-bold text-[14px] transition-all hover:shadow-md active:scale-[0.99] disabled:opacity-70"
                  data-testid="login-puvio-button">
                  <span className="inline-flex items-center justify-center w-[38px] h-[38px] rounded-full bg-card shrink-0">
                    <Fingerprint size={18} className="text-primary" strokeWidth={2.4} />
                  </span>
                  <span className="flex-1 text-left">
                    {puvioLoading ? "Opening Puvio ID…" : "Sign in with Puvio ID"}
                  </span>
                  <ArrowRight size={14} className="opacity-70" />
                </button>
                <p className="text-[11px] text-center text-muted-foreground mt-1.5">
                  <span className="font-extrabold text-foreground">Puvio ID</span> · Google-powered single sign-on
                </p>

                <button type="button" onClick={() => setRecoveryOpen(true)}
                  className="mt-3 w-full text-[12px] text-muted-foreground hover:text-primary font-bold inline-flex items-center justify-center gap-1.5 transition-colors"
                  data-testid="login-recovery-link">
                  <KeyRound size={12} /> Puvio ID account recovery
                </button>

                <p className="text-[10.5px] text-center text-muted-foreground mt-3 leading-relaxed">
                  By continuing, you agree to our{" "}
                  <a href="/legal?tab=terms" className="font-bold text-primary hover:underline">Terms</a>
                  {" "}and{" "}
                  <a href="/legal?tab=privacy" className="font-bold text-primary hover:underline">Privacy Policy</a>.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Trust badges */}
        <div className="mt-5 flex items-center justify-center gap-4">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold text-muted-foreground uppercase tracking-wider"><Lock size={11} /> Private</span>
          <span className="w-1 h-1 rounded-full bg-muted-foreground opacity-40" />
          <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold text-muted-foreground uppercase tracking-wider"><ShieldCheck size={11} /> Encrypted</span>
          <span className="w-1 h-1 rounded-full bg-muted-foreground opacity-40" />
          <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold text-muted-foreground uppercase tracking-wider"><Sparkles size={11} /> No ads</span>
        </div>

        <p className="text-[11px] text-center text-muted-foreground mt-4 italic font-medium">A calm space, just yours.</p>
        <p className="text-[10.5px] text-center text-muted-foreground mt-3 leading-relaxed" data-testid="login-owner-credit">
          © {new Date().getFullYear()} <span className="font-extrabold text-foreground">Knya.ai by Nevika Innovations Pvt. Ltd.</span>
          <br />CIN: <span className="font-extrabold text-foreground">U62090MH2026OPC472128</span>
        </p>
      </motion.div>

      <RecoverySheet open={recoveryOpen} onClose={() => setRecoveryOpen(false)} />
    </div>
  );
}
